// @greenlight-spec: 28-it-dashboard-ui.md
// @greenlight-spec: 16-api-conventions.md
import * as React from 'react';
import { toast } from 'sonner';
import { useQueryClient } from '@tanstack/react-query';

import { DataList, type DataListColumn } from '@/components/greenlight/DataList';
import { StatusBadge } from '@/components/greenlight/StatusBadge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { api } from '@/lib/api';
import { downloadTextFile } from '@/lib/download';
import type { FeedbackListItem } from '@greenlight/contract';
import type { FeedbackTriageStatus } from '@greenlight/shared';
import { BulkActionToolbar } from './BulkActionToolbar';
import { FeedbackDetailDrawer } from './FeedbackDetailDrawer';
import {
  collapseByFingerprint,
  extraHandoffLabel,
  isObserverFiled,
  statusVariant,
  type FeedbackRow,
} from './presenter';
import { useFeedbackList } from './useFeedbackList';

const SOURCE_OPTIONS = [
  { value: 'all', label: 'All sources' },
  { value: 'agent', label: 'Agent' },
  { value: 'user', label: 'User' },
];
const STATUS_OPTIONS = [
  { value: 'all', label: 'All statuses' },
  { value: 'unread', label: 'Unread' },
  { value: 'read', label: 'Read' },
  { value: 'archived', label: 'Archived' },
];
const CATEGORY_OPTIONS = [
  { value: 'all', label: 'All categories' },
  { value: 'bug', label: 'Bug' },
  { value: 'friction', label: 'Friction' },
  { value: 'suggestion', label: 'Suggestion' },
  { value: 'other', label: 'Other' },
];

// Who sent this. Name over address when the directory has one, since IT reads
// this table to answer "who is telling us this" — the address is kept beneath
// so it stays copyable and unambiguous between two people sharing a name.
function Submitter({ item }: { readonly item: FeedbackListItem }): React.JSX.Element {
  const name = item.submitted_by_name;
  const email = item.submitted_by_email;
  if (!name && !email) {
    return <span className="text-[12px] text-muted-foreground">Unknown</span>;
  }
  return (
    <span className="block min-w-0">
      <span className="block truncate text-[12px] text-foreground">{name ?? email}</span>
      {name && email && (
        <span className="block truncate text-[11px] text-muted-foreground">{email}</span>
      )}
    </span>
  );
}

/**
 * Why a hand-off produced nothing. The distinctions matter: an install that has
 * not enrolled needs to wait, a rejected payload needs a platform fix, and an
 * unreachable Shift just needs retrying.
 */
const SHARE_FAILURE_COPY: Record<string, string> = {
  not_enrolled:
    'This install has not finished enrolling with Shift yet. Try again once fleet reporting is running.',
  nothing_to_share: 'Those items have already been sent, or are archived.',
  rejected: 'Shift rejected the hand-off. This is a platform bug — please report it.',
  unavailable: 'Could not reach Shift. The items are unchanged; try again later.',
};

export function AgentFeedbackTab(): React.JSX.Element {
  const queryClient = useQueryClient();
  const list = useFeedbackList();
  const updateStatus = api.feedback.updateStatus.useMutation();

  const [selected, setSelected] = React.useState<Set<string>>(new Set());
  const [detailId, setDetailId] = React.useState<string | null>(null);
  const [exporting, setExporting] = React.useState(false);
  const [sending, setSending] = React.useState(false);

  const rows = React.useMemo(() => collapseByFingerprint(list.items), [list.items]);
  const pageIds = list.items.map((i) => i.id);
  const allOnPageSelected = pageIds.length > 0 && pageIds.every((id) => selected.has(id));
  const selectedRowKeys = new Set(
    rows.filter((row) => row.groupIds.every((id) => selected.has(id))).map((row) => row.id),
  );

  // A collapsed row stands for every report in it, so selecting it selects them all.
  function toggleRow(row: FeedbackRow<FeedbackListItem>): void {
    setSelected((prev) => {
      const next = new Set(prev);
      const on = row.groupIds.every((id) => next.has(id));
      for (const id of row.groupIds) {
        if (on) next.delete(id);
        else next.add(id);
      }
      return next;
    });
  }

  function toggleAllOnPage(): void {
    setSelected((prev) => {
      const next = new Set(prev);
      if (allOnPageSelected) pageIds.forEach((id) => next.delete(id));
      else pageIds.forEach((id) => next.add(id));
      return next;
    });
  }

  async function refresh(): Promise<void> {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ['feedback-list'] }),
      queryClient.invalidateQueries({ queryKey: ['feedback-summary'] }),
    ]);
  }

  async function applyStatus(status: FeedbackTriageStatus): Promise<void> {
    const ids = [...selected];
    if (ids.length === 0) return;
    try {
      const res = await updateStatus.mutateAsync({ body: { ids, status } });
      if (res.status >= 300) throw res;
      const updated = res.body.updated;
      if (updated === 0) {
        toast.error('None of those items could be updated.');
        return;
      }
      toast.success(`Marked ${updated} item${updated === 1 ? '' : 's'} ${status}.`);
      setSelected(new Set());
      await refresh();
    } catch {
      toast.error('Could not update those items. Please try again.');
    }
  }

  /**
   * Download for hand-off to Shift. The document is rendered server-side: only
   * the server knows the org and installation the header carries, and the shape
   * is a privacy contract rather than a presentation choice — it excludes the
   * submitter identity this table still shows for local triage.
   *
   * `sent` is recorded only after the file has actually been saved, so a failed
   * download never marks items as handed over.
   */
  async function exportSelected(): Promise<void> {
    const ids = [...selected];
    if (ids.length === 0) return;
    setExporting(true);
    try {
      const rendered = await api.feedback.exportMarkdown.mutation({ body: { ids } });
      if (rendered.status !== 200 || rendered.body.item_count === 0) throw new Error('no items');
      downloadTextFile('greenlight-feedback.md', rendered.body.markdown);

      const marked = await api.feedback.share.mutation({ body: { ids, via: 'download' } });
      const count = marked.status === 200 ? marked.body.shared : 0;
      toast.success(
        count > 0
          ? `Downloaded ${rendered.body.item_count} item${rendered.body.item_count === 1 ? '' : 's'} and marked ${count} sent.`
          : `Downloaded ${rendered.body.item_count} item${rendered.body.item_count === 1 ? '' : 's'}.`,
      );
      setSelected(new Set());
      await refresh();
    } catch {
      toast.error('Could not export the selected feedback.');
    } finally {
      setExporting(false);
    }
  }

  /**
   * Send straight to Shift over the fleet ingest. Items are marked `sent` only on
   * a 2xx, so a Shift outage leaves them in the inbox to try again rather than
   * claiming they were handed over.
   */
  async function sendToShift(): Promise<void> {
    const ids = [...selected];
    if (ids.length === 0) return;
    setSending(true);
    try {
      const res = await api.feedback.share.mutation({ body: { ids, via: 'fleet' } });
      if (res.status !== 200) throw res;
      if (res.body.reason) {
        toast.error(
          res.body.shared > 0
            ? `Sent ${res.body.shared} item${res.body.shared === 1 ? '' : 's'} to Shift, but the rest could not be handed over. They are still in the inbox — try again.`
            : SHARE_FAILURE_COPY[res.body.reason],
        );
        await refresh();
        return;
      }
      if (res.body.shared > 0) {
        toast.success(`Sent ${res.body.shared} item${res.body.shared === 1 ? '' : 's'} to Shift.`);
        setSelected(new Set());
        await refresh();
        return;
      }
      toast.error(SHARE_FAILURE_COPY.unavailable);
    } catch {
      toast.error(SHARE_FAILURE_COPY.unavailable);
    } finally {
      setSending(false);
    }
  }

  const columns: DataListColumn<FeedbackRow<FeedbackListItem>>[] = [
    {
      key: 'title',
      header: 'Title',
      primary: true,
      render: (i) =>
        i.groupIds.length > 1 ? (
          <span className="inline-flex flex-wrap items-center gap-1.5">
            {i.title}
            <StatusBadge variant="inactive" data-testid="feedback-repeat-count">
              ×{i.groupIds.length}
            </StatusBadge>
          </span>
        ) : (
          i.title
        ),
    },
    {
      key: 'source',
      header: 'Source',
      priority: 'lg',
      render: (i) => (
        <span className="inline-flex flex-wrap items-center gap-1">
          <span className="font-mono text-[11px] text-muted-foreground">
            {i.source}·{i.surface}
          </span>
          {isObserverFiled(i.context) && <StatusBadge variant="inactive">observer</StatusBadge>}
        </span>
      ),
    },
    {
      key: 'category',
      header: 'Category',
      priority: 'lg',
      render: (i) => (
        <span className="font-mono text-[11px] text-muted-foreground">{i.category}</span>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      render: (i) => (
        <span className="inline-flex flex-wrap items-center gap-1">
          <StatusBadge variant={statusVariant(i.status)}>{i.status}</StatusBadge>
          {extraHandoffLabel(i.status, i.sent_at) && (
            <StatusBadge variant="inactive">sent</StatusBadge>
          )}
        </span>
      ),
    },
    {
      key: 'submitted_by',
      header: 'Submitted by',
      render: (i) => <Submitter item={i} />,
    },
    {
      key: 'created_at',
      header: 'When',
      priority: 'md',
      render: (i) => (
        <span className="text-[12px] text-muted-foreground">
          {new Date(i.created_at).toLocaleString()}
        </span>
      ),
    },
  ];

  const selectionCount = selected.size;

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-2 md:flex-row md:items-center">
        <Select
          value={list.filters.source === '' ? 'all' : list.filters.source}
          onValueChange={(v) => list.setFilter('source', v === 'all' ? '' : v)}
        >
          <SelectTrigger className="md:w-40" aria-label="Filter by source">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {SOURCE_OPTIONS.map((o) => (
              <SelectItem key={o.value} value={o.value}>
                {o.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select
          value={list.filters.status === '' ? 'all' : list.filters.status}
          onValueChange={(v) => list.setFilter('status', v === 'all' ? '' : v)}
        >
          <SelectTrigger className="md:w-40" aria-label="Filter by status">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {STATUS_OPTIONS.map((o) => (
              <SelectItem key={o.value} value={o.value}>
                {o.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select
          value={list.filters.category === '' ? 'all' : list.filters.category}
          onValueChange={(v) => list.setFilter('category', v === 'all' ? '' : v)}
        >
          <SelectTrigger className="md:w-44" aria-label="Filter by category">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {CATEGORY_OPTIONS.map((o) => (
              <SelectItem key={o.value} value={o.value}>
                {o.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {list.hasFilters && (
          <Button variant="ghost" onClick={list.clearFilters}>
            Clear
          </Button>
        )}
      </div>

      <BulkActionToolbar
        selectionCount={selectionCount}
        actions={[
          { label: 'Mark read', onClick: () => void applyStatus('read') },
          { label: 'Mark unread', onClick: () => void applyStatus('unread') },
          { label: 'Archive', onClick: () => void applyStatus('archived') },
          {
            label: 'Download .md',
            onClick: () => void exportSelected(),
            busy: exporting,
            busyLabel: 'Exporting…',
            primary: true,
            testId: 'feedback-export',
          },
          {
            label: 'Send to Shift',
            onClick: () => void sendToShift(),
            busy: sending,
            busyLabel: 'Sending…',
            primary: true,
            testId: 'feedback-send-to-shift',
          },
        ]}
      />

      {list.isError ? (
        <Alert variant="destructive">
          <AlertDescription>Couldn&apos;t load feedback. Retry in a moment.</AlertDescription>
        </Alert>
      ) : list.isLoading ? (
        <div className="space-y-2" data-testid="feedback-loading">
          {Array.from({ length: 6 }, (_, i) => (
            <Skeleton key={i} className="h-12 w-full" />
          ))}
        </div>
      ) : (
        <>
          {list.items.length > 0 && (
            <div>
              <Button variant="ghost" size="sm" onClick={toggleAllOnPage}>
                {allOnPageSelected ? 'Clear page selection' : 'Select all on page'}
              </Button>
            </div>
          )}
          <DataList
            items={rows}
            columns={columns}
            rowKey={(i) => i.id}
            ariaLabel="Feedback"
            selectedKey={detailId ?? undefined}
            selectedKeys={selectedRowKeys}
            onToggleRow={toggleRow}
            selectionLabel={(i) => `Select "${i.title}"`}
            onRowActivate={(i) => setDetailId(i.id)}
            empty={
              <div className="rounded-gl border border-dashed border-border p-8 text-center">
                {list.hasFilters
                  ? 'No feedback matches these filters.'
                  : 'No feedback yet. Agent reports and dashboard submissions land here.'}
              </div>
            }
          />
          <div className="flex items-center justify-end gap-2">
            <Button variant="outline" size="sm" onClick={list.goPrev} disabled={!list.canGoPrev}>
              Previous
            </Button>
            <Button variant="outline" size="sm" onClick={list.goNext} disabled={!list.nextCursor}>
              Next
            </Button>
          </div>
        </>
      )}

      <FeedbackDetailDrawer feedbackId={detailId} onClose={() => setDetailId(null)} />
    </div>
  );
}
